/**
 * `GetStaffMe`（アプリ起動直後・ログイン直後に呼ぶスタッフ情報の取得）が失敗したときの扱いを決める。
 *
 * ## なぜ分類するか
 *
 * Firebaseのサインインが通った後にこのRPCが失敗する経路は2種類あり、取るべき対応が逆になる。
 *
 * - **確定的な拒否**（停止済みスタッフ / 有効な契約が無い企業 / トークン失効）:
 *   何度やっても通らない。サインアウトして理由を表示しないと、Firebaseセッションが残ったまま
 *   同じ失敗を繰り返す「画面が無反応」の状態が続く（2026-09の事象）。
 * - **一時的な失敗**（通信断・サーバ内部エラー）: サインアウトするとパスワードの再入力を強いる。
 *   セッションは保ったまま再試行できるようにする。
 *
 * 判定はConnect codeで行う。`unauthenticated` / `permission_denied` は主体そのものが
 * 拒否されているので確定的、それ以外（`unknown` / `unavailable` / `internal` /
 * `deadline_exceeded` や、そもそもConnectErrorでない例外）は一時的として扱う。
 */
import { Code } from '@connectrpc/connect'

import { parseStructuredError } from '@modules/error/errorCodes'

/** 一時的な失敗のときに再試行画面へ出す文言。サーバ由来の文言は当てにしない */
const RETRYABLE_MESSAGE =
  'ログイン情報の取得に失敗しました。通信環境を確認して、再試行してください。'

export interface StaffMeFailure {
  /** true: サインアウトして理由を表示する / false: セッションを保って再試行させる */
  fatal: boolean
  /** ユーザーに見せる文言 */
  message: string
  /** 診断・Sentry用の識別子（アプリのエラーコード、またはConnect code） */
  code: string
}

/** Connect codeが「主体そのものの拒否」を表すか。数値enumと文字列名の両方を受ける */
const isSubjectRejected = (code: unknown): boolean => {
  if (typeof code === 'number') {
    return code === Code.Unauthenticated || code === Code.PermissionDenied
  }
  if (typeof code === 'string') {
    return code === 'unauthenticated' || code === 'permission_denied'
  }
  return false
}

/**
 * `GetStaffMe` の失敗を分類する。
 *
 * @param error RPCがthrowした値（ConnectErrorを想定するが何でもよい）
 */
export const classifyStaffMeFailure = (error: unknown): StaffMeFailure => {
  const structured = parseStructuredError(error)
  const connectCode =
    error !== null && typeof error === 'object' && 'code' in error
      ? (error as { code?: unknown }).code
      : undefined

  // parseStructuredErrorはerror_info（アプリのエラーコード）が無いときConnect codeをそのまま
  // codeに入れる。ConnectErrorのcodeは数値enumなので、文字列に揃えてから扱う
  const code = String(structured.code)

  if (isSubjectRejected(connectCode)) {
    return { fatal: true, message: structured.message, code }
  }
  return { fatal: false, message: RETRYABLE_MESSAGE, code }
}
